import { useEffect, useMemo, useState } from "react";

const statusOptions = [
  { id: "open", label: "Åbne" },
  { id: "reviewed", label: "Gennemgået" },
  { id: "dismissed", label: "Afvist" },
  { id: "action_taken", label: "Handling taget" },
];

const targetOptions = [
  { id: "all", label: "Alle typer" },
  { id: "listing", label: "Annoncer" },
  { id: "message", label: "Beskeder" },
  { id: "user", label: "Brugere" },
];

function AdminModerationPage({
  activeStatus,
  activeTargetType,
  error,
  flags,
  isLoading,
  listingReports,
  messageReports,
  onBack,
  onChangeStatusFilter,
  onChangeTargetTypeFilter,
  onUpdateFlagStatus,
  onUpdateListingReportStatus,
  onUpdateMessageReportStatus,
  updatingItemKey,
}) {
  const showListings = activeTargetType === "all" || activeTargetType === "listing";
  const showMessages = activeTargetType === "all" || activeTargetType === "message";
  const showUsers = activeTargetType === "all" || activeTargetType === "user";
  const visibleFlags = flags.filter((flag) => (
    activeTargetType === "all" || flag.target_type === activeTargetType
  ));
  const queueItems = useMemo(
    () => buildQueueItems({ activeTargetType, listingReports, messageReports, visibleFlags }),
    [activeTargetType, listingReports, messageReports, visibleFlags]
  );
  const [selectedItemId, setSelectedItemId] = useState("");
  const selectedItem = queueItems.find((item) => item.id === selectedItemId) || queueItems[0] || null;

  useEffect(() => {
    if (queueItems.length === 0) {
      setSelectedItemId("");
      return;
    }

    if (!queueItems.some((item) => item.id === selectedItemId)) {
      setSelectedItemId(queueItems[0].id);
    }
  }, [queueItems, selectedItemId]);

  const hasAnyResults = (
    (showListings && listingReports.length > 0) ||
    (showMessages && messageReports.length > 0) ||
    (showUsers && visibleFlags.some((flag) => flag.target_type === "user")) ||
    visibleFlags.length > 0
  );

  return (
    <section className="main-panel">
      <div className="page-heading moderation-heading">
        <div>
          <span className="mini-label">Admin moderation</span>
          <h1>Moderationsko</h1>
          <p>Se automatiske signaler og opdater status, når noget er vurderet.</p>
        </div>
        <button className="secondary-action page-action" onClick={onBack} type="button">
          Tilbage
        </button>
      </div>

      <div className="moderation-toolbar">
        <div className="sales-tabs" role="tablist" aria-label="Moderation status">
          {statusOptions.map((option) => (
            <button
              className={activeStatus === option.id ? "active" : ""}
              key={option.id}
              onClick={() => onChangeStatusFilter(option.id)}
              type="button"
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="category-tabs" role="tablist" aria-label="Moderation typer">
          {targetOptions.map((option) => (
            <button
              className={activeTargetType === option.id ? "active" : ""}
              key={option.id}
              onClick={() => onChangeTargetTypeFilter(option.id)}
              type="button"
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {error && <p className="status-line error">{error}</p>}

      {isLoading ? (
        <div className="empty-results">
          <strong>Henter moderation...</strong>
        </div>
      ) : !hasAnyResults ? (
        <div className="empty-results">
          <strong>Ingen moderationstråde fundet</strong>
          <p>Der er ingen flag, som matcher de valgte filtre lige nu.</p>
        </div>
      ) : (
        <div className="moderation-workspace">
          <div className="moderation-sections">
            {showListings && (
              <section className="moderation-section">
                <div className="moderation-section-header">
                  <div>
                    <span className="mini-label">Brugerrapporter</span>
                    <h2>Annonce-rapporter</h2>
                  </div>
                  <strong>{listingReports.length}</strong>
                </div>
                <div className="moderation-list">
                  {listingReports.length === 0 ? (
                    <div className="empty-results compact-empty">
                      <p>Ingen annonce-rapporter i dette filter.</p>
                    </div>
                  ) : (
                    listingReports.map((report) => {
                      const itemId = `listing-report:${report.id}`;
                      return (
                        <article
                          className={`moderation-card moderation-card-selectable ${selectedItem?.id === itemId ? "active" : ""}`}
                          key={report.id}
                          onClick={() => setSelectedItemId(itemId)}
                        >
                          <div className="moderation-card-header">
                            <div>
                              <span className="mini-label">Annonce</span>
                              <h2>{report.listing_title || "Annonce"}</h2>
                              <p>{report.reason}</p>
                            </div>
                            <span className={`status-pill ${report.status === "dismissed" ? "sold" : ""}`}>
                              {statusLabel(report.status)}
                            </span>
                          </div>
                          <div className="moderation-meta">
                            <span>Reporter</span><strong>{report.reporter_display_name}</strong>
                            <span>Flag i alt</span><strong>{report.moderation_flag_count}</strong>
                            <span>Åbne flag</span><strong>{report.open_moderation_flag_count}</strong>
                            <span>Seneste flag</span><strong>{report.latest_moderation_flag_rule_key || "Ingen"}</strong>
                          </div>
                        </article>
                      );
                    })
                  )}
                </div>
              </section>
            )}

            {showMessages && (
              <section className="moderation-section">
                <div className="moderation-section-header">
                  <div>
                    <span className="mini-label">Brugerrapporter</span>
                    <h2>Besked-rapporter</h2>
                  </div>
                  <strong>{messageReports.length}</strong>
                </div>
                <div className="moderation-list">
                  {messageReports.length === 0 ? (
                    <div className="empty-results compact-empty">
                      <p>Ingen besked-rapporter i dette filter.</p>
                    </div>
                  ) : (
                    messageReports.map((report) => {
                      const itemId = `message-report:${report.id}`;
                      return (
                        <article
                          className={`moderation-card moderation-card-selectable ${selectedItem?.id === itemId ? "active" : ""}`}
                          key={report.id}
                          onClick={() => setSelectedItemId(itemId)}
                        >
                          <div className="moderation-card-header">
                            <div>
                              <span className="mini-label">Besked</span>
                              <h2>{report.message_body || "Rapporteret besked"}</h2>
                              <p>{report.reason}</p>
                            </div>
                            <span className={`status-pill ${report.status === "dismissed" ? "sold" : ""}`}>
                              {statusLabel(report.status)}
                            </span>
                          </div>
                          <div className="moderation-meta">
                            <span>Reporter</span><strong>{report.reporter_display_name}</strong>
                            <span>Tråd</span><strong>{report.conversation_id || "Ukendt"}</strong>
                            <span>Flag i alt</span><strong>{report.moderation_flag_count}</strong>
                            <span>Seneste flag</span><strong>{report.latest_moderation_flag_rule_key || "Ingen"}</strong>
                          </div>
                        </article>
                      );
                    })
                  )}
                </div>
              </section>
            )}

            <div className="moderation-section-header">
              <div>
                <span className="mini-label">Systemsignaler</span>
                <h2>Moderation flags</h2>
              </div>
              <strong>{visibleFlags.length}</strong>
            </div>
            <div className="moderation-list">
              {visibleFlags.length === 0 ? (
                <div className="empty-results compact-empty">
                  <p>Ingen moderation flags i dette filter.</p>
                </div>
              ) : (
                visibleFlags.map((flag) => {
                  const itemId = `flag:${flag.id}`;
                  return (
                    <article
                      className={`moderation-card moderation-card-selectable ${selectedItem?.id === itemId ? "active" : ""}`}
                      key={flag.id}
                      onClick={() => setSelectedItemId(itemId)}
                    >
                      <div className="moderation-card-header">
                        <div>
                          <span className="mini-label">{targetLabel(flag.target_type)}</span>
                          <h2>{headline(flag)}</h2>
                          <p>{flag.reason}</p>
                        </div>
                        <span className={`status-pill ${flag.status === "dismissed" ? "sold" : ""}`}>
                          {statusLabel(flag.status)}
                        </span>
                      </div>

                      <div className="moderation-meta">
                        <span>Regel</span><strong>{flag.rule_key}</strong>
                        <span>Kilde</span><strong>{flag.source}</strong>
                        <span>Oprettet</span><strong>{formatDate(flag.created_at)}</strong>
                        <span>Sidst vurderet</span><strong>{formatDate(flag.reviewed_at) || "Ikke endnu"}</strong>
                      </div>
                    </article>
                  );
                })
              )}
            </div>
          </div>

          <aside className="moderation-detail-panel">
            {selectedItem ? (
              <ModerationDetailPanel
                item={selectedItem}
                onUpdateFlagStatus={onUpdateFlagStatus}
                onUpdateListingReportStatus={onUpdateListingReportStatus}
                onUpdateMessageReportStatus={onUpdateMessageReportStatus}
                updatingItemKey={updatingItemKey}
              />
            ) : (
              <div className="empty-results compact-empty">
                <p>Vælg en tråd for at se detaljer.</p>
              </div>
            )}
          </aside>
        </div>
      )}
    </section>
  );
}

function ModerationDetailPanel({
  item,
  onUpdateFlagStatus,
  onUpdateListingReportStatus,
  onUpdateMessageReportStatus,
  updatingItemKey,
}) {
  const isUpdating = updatingItemKey === item.id;

  return (
    <section className="profile-card moderation-detail-card">
      <span className="mini-label">{detailEyebrow(item)}</span>
      <h2>{item.title}</h2>
      <p className="moderation-detail-description">{item.reason}</p>

      <div className="moderation-meta">
        <span>Status</span><strong>{statusLabel(item.status)}</strong>
        <span>Oprettet</span><strong>{formatDate(item.created_at)}</strong>
        <span>Sidst vurderet</span><strong>{formatDate(item.reviewed_at) || "Ikke endnu"}</strong>
        {item.reporterDisplayName ? (
          <>
            <span>Reporter</span><strong>{item.reporterDisplayName}</strong>
          </>
        ) : null}
        {item.ruleKey ? (
          <>
            <span>Regel</span><strong>{item.ruleKey}</strong>
          </>
        ) : null}
        {item.source ? (
          <>
            <span>Kilde</span><strong>{item.source}</strong>
          </>
        ) : null}
        {item.conversationId ? (
          <>
            <span>Tråd</span><strong>{item.conversationId}</strong>
          </>
        ) : null}
        {item.moderationFlagCount !== undefined ? (
          <>
            <span>Flag i alt</span><strong>{item.moderationFlagCount}</strong>
            <span>Åbne flag</span><strong>{item.openModerationFlagCount}</strong>
          </>
        ) : null}
      </div>

      {item.body ? (
        <div className="moderation-body-copy">
          <strong>Indhold</strong>
          <p>{item.body}</p>
        </div>
      ) : null}

      {item.detailsText ? (
        <div className="moderation-body-copy">
          <strong>Detaljer</strong>
          <p>{item.detailsText}</p>
        </div>
      ) : null}

      {item.detailsBadges?.length ? (
        <div className="moderation-details">
          {item.detailsBadges.map((detail) => (
            <span key={`${detail.key}-${detail.value}`}>
              <strong>{keyLabel(detail.key)}:</strong> {String(detail.value)}
            </span>
          ))}
        </div>
      ) : null}

      <StatusActions
        currentStatus={item.status}
        isUpdating={isUpdating}
        onAction={(status) => performItemAction(item, status, {
          onUpdateFlagStatus,
          onUpdateListingReportStatus,
          onUpdateMessageReportStatus,
        })}
      />
    </section>
  );
}

function StatusActions({ currentStatus, isUpdating, onAction }) {
  return (
    <div className="sale-actions moderation-actions">
      <button
        disabled={isUpdating || currentStatus === "reviewed"}
        onClick={() => onAction("reviewed")}
        type="button"
      >
        {isUpdating ? "Gemmer..." : "Marker gennemgået"}
      </button>
      <button
        disabled={isUpdating || currentStatus === "dismissed"}
        onClick={() => onAction("dismissed")}
        type="button"
      >
        Afvis
      </button>
      <button
        disabled={isUpdating || currentStatus === "action_taken"}
        onClick={() => onAction("action_taken")}
        type="button"
      >
        Handling taget
      </button>
    </div>
  );
}

function performItemAction(item, status, handlers) {
  if (item.kind === "flag") {
    handlers.onUpdateFlagStatus(item.entityId, status);
    return;
  }
  if (item.kind === "listing-report") {
    handlers.onUpdateListingReportStatus(item.entityId, status);
    return;
  }
  handlers.onUpdateMessageReportStatus(item.entityId, status);
}

function buildQueueItems({ activeTargetType, listingReports, messageReports, visibleFlags }) {
  const items = [];

  if (activeTargetType === "all" || activeTargetType === "listing") {
    listingReports.forEach((report) => {
      items.push({
        id: `listing-report:${report.id}`,
        entityId: report.id,
        kind: "listing-report",
        title: report.listing_title || "Annonce",
        reason: report.reason,
        status: report.status,
        created_at: report.created_at,
        reviewed_at: report.reviewed_at,
        reporterDisplayName: report.reporter_display_name,
        moderationFlagCount: report.moderation_flag_count,
        openModerationFlagCount: report.open_moderation_flag_count,
        detailsText: report.details,
      });
    });
  }

  if (activeTargetType === "all" || activeTargetType === "message") {
    messageReports.forEach((report) => {
      items.push({
        id: `message-report:${report.id}`,
        entityId: report.id,
        kind: "message-report",
        title: report.message_body || "Rapporteret besked",
        reason: report.reason,
        status: report.status,
        created_at: report.created_at,
        reviewed_at: report.reviewed_at,
        reporterDisplayName: report.reporter_display_name,
        moderationFlagCount: report.moderation_flag_count,
        openModerationFlagCount: report.open_moderation_flag_count,
        conversationId: report.conversation_id,
        detailsText: report.details,
        body: report.message_body,
      });
    });
  }

  visibleFlags.forEach((flag) => {
    items.push({
      id: `flag:${flag.id}`,
      entityId: flag.id,
      kind: "flag",
      title: headline(flag),
      reason: flag.reason,
      status: flag.status,
      created_at: flag.created_at,
      reviewed_at: flag.reviewed_at,
      ruleKey: flag.rule_key,
      source: flag.source,
      conversationId: flag.conversation_id,
      body: flag.message_body,
      detailsBadges: flag.details
        ? Object.entries(flag.details).map(([key, value]) => ({ key, value }))
        : [],
      targetType: flag.target_type,
    });
  });

  return items.sort((left, right) => {
    const leftTime = Date.parse(left.created_at || "") || 0;
    const rightTime = Date.parse(right.created_at || "") || 0;
    return rightTime - leftTime;
  });
}

function headline(flag) {
  if (flag.listing_title) {
    return flag.listing_title;
  }
  if (flag.message_body) {
    return flag.message_body;
  }
  if (flag.user_display_name) {
    return flag.user_display_name;
  }
  return "Moderationsflag";
}

function targetLabel(targetType) {
  const labels = {
    listing: "Annonce",
    message: "Besked",
    user: "Bruger",
  };
  return labels[targetType] || targetType;
}

function statusLabel(status) {
  const labels = {
    open: "Åben",
    reviewed: "Gennemgået",
    dismissed: "Afvist",
    action_taken: "Handling taget",
  };
  return labels[status] || status;
}

function formatDate(value) {
  if (!value) {
    return "";
  }

  try {
    return new Intl.DateTimeFormat("da-DK", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function keyLabel(key) {
  const labels = {
    latest_report_reason: "Seneste grund",
    latest_reporter_id: "Seneste reporter",
    report_count: "Antal rapporter",
    conversation_count: "Antal samtaler",
    duplicate_count: "Dubletter",
  };
  return labels[key] || key;
}

function detailEyebrow(item) {
  const labels = {
    flag: "Systemsignal",
    "listing-report": "Annonce-rapport",
    "message-report": "Besked-rapport",
  };
  return labels[item.kind] || "Moderation";
}

export default AdminModerationPage;
